/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { AgentVersionService } from '../services/agentVersionService';
import { AgentVersion } from '../types/AgentVersion';
import { WorkflowBaseService } from '@/features/Workflows/services/WorkflowBaseService';
import { cleanWorkflowSchema } from '@/features/Workflows/utils/workflow-cleaner';

interface PublishVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (version: AgentVersion) => void;
  agentBaseId: string;
  agentName: string;
  workflowSchema: any;
  settings?: Record<string, any>;
  workflowSettings?: any;
  prompts?: any;
  logoUrl?: string;
  workspaceId: string;
  createdBy: string;
  organizationId?: string;
}

export function PublishVersionModal({
  isOpen,
  onClose,
  onSuccess,
  agentBaseId,
  agentName,
  workflowSchema,
  settings = {},
  workflowSettings,
  prompts,
  logoUrl,
  workspaceId,
  createdBy,
  organizationId
}: PublishVersionModalProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [nextVersion, setNextVersion] = useState('');
  const [description, setDescription] = useState('');
  const publishingRef = useRef(false);

  // Buscar o próximo número de versão quando o modal abrir
  useEffect(() => {
    if (isOpen && agentBaseId) {
      fetchNextVersion();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agentBaseId]);

  const fetchNextVersion = async () => {
    setIsLoadingVersion(true);
    try {
      const { data, error } = await AgentVersionService.getNextVersionNumber(agentBaseId);
      
      if (error) {
        toast.error(t('agents.settings.publishButton.error'));
        return;
      }

      if (data?.nextVersion) {
        setNextVersion(data.nextVersion);
      } else {
        // Fallback: definir versão padrão se não conseguir buscar
        setNextVersion('v1.0.0');
      }
    } catch (error) {
      toast.error(t('agents.settings.publishButton.error'));
      // Fallback: definir versão padrão em caso de erro
      setNextVersion('v1.0.0');
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const handlePublish = async () => {
    // Guard contra cliques múltiplos rápidos
    if (isLoading || publishingRef.current) return;
    publishingRef.current = true;
    if (!nextVersion) {
      toast.error(t('agents.settings.publishButton.versionNameRequired'));
      publishingRef.current = false;
      return;
    }

    // Preparar schema e settings do workflow
    let finalWorkflowSchema = workflowSchema ? { ...workflowSchema } : { nodes: [], edges: [] };

    // Garantir que workflow_base_id seja preservado em todos os cenários
    if (!finalWorkflowSchema?.workflow_base_id && workflowSchema?.workflow_base_id) {
      finalWorkflowSchema.workflow_base_id = workflowSchema.workflow_base_id;
    }
    let finalWorkflowSettings = workflowSettings ? { ...workflowSettings } : undefined;

    // Fallback simplificado: buscar workflow_base se faltar workflow_base_id ou workflow_settings
    // IMPORTANTE: NÃO sobrescrever prompts e parameters - eles vêm das props (dados locais)
    if (!finalWorkflowSchema?.workflow_base_id || !finalWorkflowSettings) {
      try {
        const workflows = await WorkflowBaseService.getByAgentBaseId(agentBaseId)
          .then((list: any[]) => Array.isArray(list) ? list : []);

        const workflow = workflows.length > 0 ? workflows[0] : null;
        if (workflow) {
          // Apenas pegar nodes, edges e workflow_base_id do banco
          // Prompts e parameters devem vir das props (dados locais editados pelo usuário)
          finalWorkflowSchema = {
            workflow_base_id: workflow.id,
            nodes: workflow.settings?.nodes || [],
            edges: workflow.settings?.edges || []
            // NÃO incluir prompts e parameters aqui - eles vêm das props!
          };
          finalWorkflowSettings = {
            ...(workflow.settings || {}),
            // Preservar prompts e parameters das props se existirem
            prompts: prompts || settings?.prompts || workflow.settings?.prompts || [],
            parameters: settings?.parameters || workflow.settings?.parameters || {}
          };
        }
      } catch (e) {
        // Mantém schema básico mesmo se falhar; backend validará
      }
    }

    setIsLoading(true);
    try {
      // Limpar workflow schema usando cleanWorkflowSchema para converter {type, value} para valores diretos
      let cleanedWorkflowSchema;
      try {
        const resolvedOrganizationId =
          organizationId ||
          finalWorkflowSettings?.organizationId ||
          finalWorkflowSchema?.organizationId ||
          settings?.organizationId;

        cleanedWorkflowSchema = await cleanWorkflowSchema(
          finalWorkflowSchema?.nodes || [],
          finalWorkflowSchema?.edges || [],
          {
            organizationId: resolvedOrganizationId,
            workspaceId,
            workflowBaseId: finalWorkflowSchema?.workflow_base_id
          }
        );
      } catch (cleanError) {
        // Falha ao limpar: usar schema original como fallback
        cleanedWorkflowSchema = finalWorkflowSchema;
      }

      const resolvePrompts = (value: any) =>
        Array.isArray(value) && value.length > 0 ? value : undefined;

      // PRIORIDADE: props > settings > workflow (dados locais têm prioridade sobre banco)
      const schemaPrompts =
        resolvePrompts(prompts) ??
        resolvePrompts(settings?.prompts) ??
        resolvePrompts(finalWorkflowSettings?.prompts) ??
        resolvePrompts(finalWorkflowSchema?.prompts) ??
        [];
      
      cleanedWorkflowSchema.prompts = schemaPrompts;
      finalWorkflowSettings = {
        ...(finalWorkflowSettings || {}),
        prompts: schemaPrompts
      };

      // Prioridade: dados locais (settings.parameters) > dados do banco (workflow)
      const candidateParametersSources = [
        settings?.parameters,
        finalWorkflowSettings?.parameters,
        finalWorkflowSchema?.parameters
      ];

      const populatedParameters = candidateParametersSources.find((value) => {
        if (!value) return false;
        const paramValues = value.values ?? value;
        return paramValues && Object.keys(paramValues).length > 0;
      });

      const schemaParameters =
        populatedParameters ??
        candidateParametersSources.find((value) => value !== undefined && value !== null) ??
        {};

      const normalizedParameterValues = schemaParameters?.values ?? schemaParameters ?? {};
      const parameterFields =
        schemaParameters?.fields ??
        finalWorkflowSettings?.parameters?.fields ??
        settings?.parameters?.fields;

      cleanedWorkflowSchema.parameters = normalizedParameterValues;
      finalWorkflowSettings = {
        ...(finalWorkflowSettings || {}),
        parameters: {
          ...(parameterFields ? { fields: parameterFields } : {}),
          values: normalizedParameterValues
        }
      };

      const versionData = {
        name: agentName,
        version: nextVersion,
        agent_base_id: agentBaseId,
        product_id: workspaceId,
        created_by: createdBy,
        workflow_schema: cleanedWorkflowSchema,
        description: description.trim() || undefined,
        settings,
        workflow_settings: finalWorkflowSettings,
        prompts: schemaPrompts,
        logo_url: logoUrl
      };

      const { data, error } = await AgentVersionService.publishVersionSimplified(versionData);

      if (error) {
        toast.error(t('agents.settings.publishButton.error'));
        return;
      }

      toast.success(t('agents.settings.publishButton.success'));
      if (data) onSuccess(data);
      onClose();
      
      // Limpar campos
      setDescription('');
    } catch (error) {
      toast.error(t('agents.settings.publishButton.error'));
    } finally {
      setIsLoading(false);
      publishingRef.current = false;
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setDescription('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('agents.settings.publishButton.confirmTitle')}</DialogTitle>
          <DialogDescription>
            {t('agents.settings.publishButton.confirmDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Campo de versão (bloqueado) */}
          <div className="grid gap-2">
            <Label htmlFor="version">{t('agents.settings.publishButton.versionNameLabel')}</Label>
            <div className="relative">
              <Input
                id="version"
                value={isLoadingVersion ? t('common.loading') : nextVersion}
                disabled
                className="pr-10 bg-muted"
                placeholder={t('agents.settings.publishButton.versionNamePlaceholder')}
              />
              <Lock className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-xs text-muted-foreground">
              {t('agents.settings.publishButton.versionAutoGenerated')}
            </p>
          </div>

          {/* Campo de descrição (opcional) */}
          <div className="grid gap-2">
            <Label htmlFor="description">
              {t('agents.settings.publishButton.versionDescriptionLabel')}
              <span className="text-muted-foreground ml-1">({t('common.optional')})</span>
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('agents.settings.publishButton.versionDescriptionPlaceholder')}
              className="min-h-[80px]"
              maxLength={500}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/500 {t('common.characters')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t('agents.settings.publishButton.cancel')}
          </Button>
          <Button
            onClick={handlePublish}
            disabled={isLoading || isLoadingVersion || !nextVersion}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('agents.settings.publishButton.publishing')}
              </>
            ) : (
              t('agents.settings.publishButton.confirm')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
